<script setup lang="ts">
import {onMounted, onUnmounted, ref} from 'vue';
import BaseButton from "@/core/components/base/BaseButton.vue";
import ApodisCoinButton from "@/experience/components/ApodisCoinButton.vue";
import {Popover, PopoverContent, PopoverTrigger} from "@/core/components/ui/popover";
import {Link} from "@lucide/vue";


const currentButtonIndex = ref(0);
const intervalId = ref<NodeJS.Timeout | null>(null);
const popoverOpen = ref(false);

/**
 * Start the rotation loop for the animated button.
 */
const startLoop = () => {
    if (!popoverOpen.value) {
        intervalId.value = setInterval(() => {
            const maxIndex = 2;
            currentButtonIndex.value = (currentButtonIndex.value + 1) % maxIndex;
        }, 5000);
    }
};

/**
 * Stop the rotation loop.
 */
const stopLoop = () => {
    if (intervalId.value) {
        clearInterval(intervalId.value);
        intervalId.value = null;
    }
};

/**
 *
 * If popover is open, stop the rotation loop.
 */
const handlePopover = (value: boolean) => {
    popoverOpen.value = value;

    if (value) {
        stopLoop();
    } else {
        startLoop();
    }
};

onMounted(() => {
    startLoop();
});

onUnmounted(() => {
    stopLoop();
});
</script>

<template>
  <Popover modal :open="popoverOpen" @update:open="handlePopover">
    <PopoverTrigger as-child>
      <div class="cursor-pointer">
        <Transition
          name="flip-x"
          mode="out-in"
        >
          <div :key="currentButtonIndex">
            <BaseButton
              v-if="currentButtonIndex === 0"
              density="comfortable"
              class="text-black tracking-normal"
              @mouseenter="stopLoop"
              @mouseleave="startLoop"
            >
              <Link class="mr-1 w-4 h-4" />
              <span>Obtenir une réduction <span class="text-primary">en parrainant un ami</span></span>
            </BaseButton>

            <ApodisCoinButton
              v-else-if="currentButtonIndex === 1"
              icon-left
              @mouseenter="stopLoop"
              @mouseleave="startLoop"
            >
              <span>Obtenir une réduction <span class="text-primary"> en utilisant mes Apodis Coins</span></span>
            </ApodisCoinButton>
          </div>
        </Transition>
      </div>
    </PopoverTrigger>

    <PopoverContent class="p-4 w-auto space-y-2" align="start">
      <BaseButton
        density="comfortable"
        :to="{name: 'sponsorship'}"
        class="text-black tracking-normal w-full"
      >
        <Link class="mr-1 w-4 h-4 text-black" />
        <span class="text-primary">Parrainer un ami</span>
      </BaseButton>

      <div class="buttons-bottom">
        <ApodisCoinButton
          :to="{name: 'experience'}"
          icon-left
          class="w-full"
        >
          <span class="text-primary">Utiliser mes Apodis Coins</span>
        </ApodisCoinButton>
      </div>
    </PopoverContent>
  </Popover>
</template>

<style scoped>
.button-top {
    animation: slideInTop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.button-middle {
    animation: slideInMiddle 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.1s both;
}

.button-bottom {
    animation: slideInBottom 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.2s both;
}

.button-wrapper:hover {
    transform: scale(1.02);
}

/* Slide in animations */
@keyframes slideInTop {
    0% {
        opacity: 0;
        transform: translateY(-20px) rotate(-10deg) scale(0.8);
    }
    100% {
        opacity: 1;
        transform: translateY(0) rotate(0deg) scale(1);
    }
}

@keyframes slideInMiddle {
    0% {
        opacity: 0;
        transform: scale(0.8);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes slideInBottom {
    0% {
        opacity: 0;
        transform: translateY(20px) rotate(10deg) scale(0.8);
    }
    100% {
        opacity: 1;
        transform: translateY(0) rotate(0deg) scale(1);
    }
}

.flip-x-enter-active,
.flip-x-leave-active {
    transition: all 0.1s linear;
    transform-origin: center center;
    will-change: transform;
}

.flip-x-enter-from {
    transform: rotateX(-90deg) translateY(-50%);
}

.flip-x-leave-to {
    transform: rotateX(90deg) translateY(50%);
}

.flip-x-enter-to,
.flip-x-leave-from {
    transform: rotateX(0deg) translateY(0);
}

/* Enhanced transition effects */
.flip-x-enter-active {
    animation: flip-x-in 0.1s linear;
}

.flip-x-leave-active {
    animation: flip-x-out 0.1s linear;
}

@keyframes flip-x-in {
    0% {
        transform: rotateX(-90deg);
    }
    50% {
        transform: rotateX(-45deg);
    }
    100% {
        transform: rotateX(0deg);
    }
}

@keyframes flip-x-out {
    0% {
        transform: rotateX(0deg);
    }
    50% {
        transform: rotateX(45deg);
    }
    100% {
        transform: rotateX(90deg);
    }
}
</style>
